import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function CartPage() {
  const { cart, removeFromCart, updateQuantity, getTotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    // Allow both logged-in users and guests to proceed to checkout
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="empty-cart">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-4" />
          <h2 className="font-serif text-3xl font-bold text-[#1a3a5c] mb-4">Your Cart is Empty</h2>
          <p className="text-gray-600 mb-6">Discover our exquisite collection and add items to your cart</p>
          <Link
            to="/products"
            className="inline-block bg-[#1a3a5c] text-white px-8 py-3 rounded-sm font-serif tracking-wide hover:bg-[#1f4268] transition-all duration-300"
            data-testid="shop-now-button"
          >
            Shop Now
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-12" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 md:mb-8"
        >
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a3a5c] mb-2" data-testid="cart-title">
            Shopping Cart
          </h1>
          <p className="text-gray-600">{cart.length} items in your cart</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <motion.div
                key={item.product.id}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white p-4 md:p-6 rounded-lg border border-gray-200"
                data-testid={`cart-item-${item.product.id}`}
              >
                <div className="flex gap-4 md:gap-6">
                  <img
                    src={item.product.images[0]}
                    alt={item.product.name}
                    className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-lg flex-shrink-0"
                  />

                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-base md:text-xl font-semibold text-[#1a3a5c] mb-1 truncate">
                      {item.product.name}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">
                      {item.product.category} • {item.product.metal_type} • {item.product.weight}g
                    </p>
                    <p className="text-base md:text-lg font-semibold text-[#1a3a5c]" data-testid={`item-price-${item.product.id}`}>
                      ₹{item.price.totalPrice}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 md:gap-4 mt-3 md:mt-4">
                      <div className="flex items-center border border-gray-300 rounded-sm">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="px-2 md:px-3 py-1 hover:bg-gray-100 transition-colors"
                          data-testid={`decrease-quantity-${item.product.id}`}
                        >
                          <Minus className="h-3 w-3 md:h-4 md:w-4" />
                        </button>
                        <span className="px-3 md:px-4 py-1 border-x border-gray-300 text-sm md:text-base" data-testid={`quantity-${item.product.id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="px-2 md:px-3 py-1 hover:bg-gray-100 transition-colors"
                          data-testid={`increase-quantity-${item.product.id}`}
                        >
                          <Plus className="h-3 w-3 md:h-4 md:w-4" />
                        </button>
                      </div>

                      <button
                        onClick={() => {
                          removeFromCart(item.product.id);
                          toast.success('Removed from cart');
                        }}
                        className="text-red-600 hover:text-red-700 flex items-center space-x-1"
                        data-testid={`remove-item-${item.product.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="text-xs md:text-sm">Remove</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:sticky lg:top-24 h-fit"
          >
            <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
              <h3 className="font-serif text-lg md:text-xl font-semibold text-[#1a3a5c] mb-4 md:mb-6">Order Summary</h3>

              <div className="space-y-3 mb-4 md:mb-6">
                <div className="flex justify-between text-gray-600 text-sm md:text-base">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">₹{getTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600 text-sm md:text-base">
                  <span>Shipping</span>
                  <span className="text-green-600">Free</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-base md:text-lg">
                  <span className="text-[#1a3a5c]">Total</span>
                  <span className="text-[#1a3a5c]" data-testid="cart-total">₹{getTotal().toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full bg-[#1a3a5c] text-white py-3 rounded-sm font-serif tracking-wide hover:bg-[#1f4268] transition-all duration-300 btn-primary text-sm md:text-base"
                data-testid="checkout-button"
              >
                Proceed to Checkout
              </button>

              <Link
                to="/products"
                className="block text-center mt-4 text-[#1a3a5c] hover:text-[#D4AF37] transition-colors text-sm"
              >
                Continue Shopping
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}